// The ops console's fetch: same origin, the `vd_ops_session` cookie, and `x-csrf-token` on every
// write. It never sends an Authorization header (the server refuses bearer tokens here).
import { API_BASE } from "../config";
import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

let csrfToken: string | null = null;

/** From `OpsMe.csrf_token`, after sign-in and on every `/v1/platform/me`. */
export function setCsrfToken(token: string | null | undefined): void {
  csrfToken = token ?? null;
}

type Query = Record<string, string | number | boolean | null | undefined>;

export function buildQuery(q: Query | undefined): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

function url(path: string, q: Query | undefined): string {
  const base = API_BASE || window.location.origin;
  return new URL(`${path}${buildQuery(q)}`, `${base}/`).toString();
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  let res: Response;
  try {
    res = await fetch(url(path, opts.query), {
      method,
      headers,
      credentials: "include",
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Can't reach the server. Check your connection and try again.");
  }
  const requestId = res.headers.get("x-request-id") ?? undefined;
  if (!res.ok) {
    let code = "http_error";
    let message = `The server returned ${res.status}.`;
    try {
      const body = (await res.json()) as ApiErrorBody;
      if (body?.error) {
        code = body.error.code;
        message = body.error.message;
      }
    } catch {
      // Body was not JSON; keep the generic message.
    }
    if (res.status === 401 && onUnauthorized) onUnauthorized();
    throw new ApiError(res.status, code, message, requestId);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** A refused staff sign-in reads the same everywhere. */
export const NOT_STAFF = "This account is not Kite staff.";

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.code === "not_staff" ? NOT_STAFF : e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong.";
}
